import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, LogOut, Trash2, Link2, FileDown, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmployeeDialog } from "./EmployeeDialog";
import { ExitDialog } from "./ExitDialog";

const EXIT_DOCS = [["stc", "Reçu solde de tout compte"], ["certificate", "Certificat de travail"], ["france_travail", "Récap France Travail"]];
const fr = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d || "—");

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));

export function EmployeesTab({ api, societeId, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeLeft, setIncludeLeft] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [exitFor, setExitFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/employees", { params: { societe_id: societeId, include_left: includeLeft } });
      setList(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [api, societeId, includeLeft]);
  useEffect(() => { load(); }, [load]);

  const cancelExit = async (e) => {
    if (!window.confirm(`Annuler la sortie planifiée de ${e.first_name} ${e.last_name} ?`)) return;
    try {
      await api.delete(`/payroll/employees/${e.employee_id}/exit`);
      toast.success("Sortie annulée");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Action impossible"); }
  };
  const openDoc = async (e, kind) => {
    try {
      const r = await api.get(`/payroll/employees/${e.employee_id}/exit/documents/${kind}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (err) { toast.error("Document indisponible"); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Supprimer définitivement le dossier de ${e.first_name} ${e.last_name} ?`)) return;
    try {
      await api.delete(`/payroll/employees/${e.employee_id}`);
      toast.success("Dossier supprimé");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Suppression impossible"); }
  };

  return (
    <div className="space-y-3" data-testid="employees-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={includeLeft} onChange={(e) => setIncludeLeft(e.target.checked)} data-testid="include-left-toggle" /> Inclure les salariés sortis</label>
        <div className="flex-1" />
        <button onClick={() => setDialog({})} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f7c935] text-[#0b0811] text-xs font-bold hover:bg-[#ffd75a]" data-testid="employee-add-btn"><Plus className="w-3.5 h-3.5" /> Nouveau salarié</button>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div> : list.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid="employees-empty">Aucun dossier salarié. Créez le premier dossier pour lancer la paie.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((e) => (
            <div key={e.employee_id} className={`bg-[#0b0811] border rounded-xl p-4 ${e.status === "left" ? "border-white/5 opacity-60" : "border-white/10"}`} data-testid={`employee-card-${e.employee_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-white font-bold">{e.civility} {e.first_name} {e.last_name} <span className="text-[10px] font-mono text-slate-500 ml-1">{e.matricule}</span></div>
                  <div className="text-xs text-slate-400">{e.contract?.job_title || "—"} · {e.contract?.status === "cadre" ? "Cadre" : "Non-cadre"} · {e.contract?.type} depuis le {e.contract?.start_date}</div>
                </div>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${e.status === "left" ? "bg-slate-500/20 text-slate-400" : "bg-emerald-500/15 text-emerald-400"}`}>{e.status === "left" ? "SORTI" : "ACTIF"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                <div><div className="text-slate-500">Brut mensuel</div><div className="text-white font-mono font-bold">{eur(e.contract?.gross_monthly)}</div></div>
                <div><div className="text-slate-500">Durée</div><div className="text-white">{e.contract?.hours_per_month} h/mois</div></div>
                <div><div className="text-slate-500">PAS</div><div className="text-white">{e.pas?.mode === "personalized" ? `${e.pas.rate} %` : "taux neutre"}</div></div>
                <div><div className="text-slate-500">N° SS</div><div className="text-slate-300 font-mono">{e.nir_masked || "—"}</div></div>
                <div><div className="text-slate-500">IBAN</div><div className="text-slate-300 font-mono">{e.iban_masked || "—"}</div></div>
                <div><div className="text-slate-500">Solde CP</div><div className="text-white">{e.leave?.cp_balance ?? 0} j</div></div>
              </div>
              {e.exit && (
                <div className={`mt-3 rounded-lg border p-2.5 text-[11px] ${e.exit.status === "finalized" ? "border-slate-500/30 bg-slate-500/10" : "border-amber-500/30 bg-amber-500/10"}`} data-testid={`employee-exit-${e.employee_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className={e.exit.status === "finalized" ? "text-slate-300" : "text-amber-300"}>
                      <LogOut className="w-3 h-3 inline mr-1" />{e.exit.status === "finalized" ? "Sorti le" : "Sortie planifiée le"} <b>{fr(e.exit.end_date)}</b> · {e.exit.reason_label}
                      {e.exit.total_indemnities > 0 && <span className="text-slate-400"> · indemnités {eur(e.exit.total_indemnities)}</span>}
                    </span>
                    {e.exit.status !== "finalized" && (
                      <button onClick={() => cancelExit(e)} className="text-[10px] text-slate-400 hover:text-red-300 flex items-center gap-1" data-testid={`employee-exit-cancel-${e.employee_id}`}><XCircle className="w-3 h-3" /> Annuler</button>
                    )}
                  </div>
                  {e.exit.status === "finalized" && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {EXIT_DOCS.map(([k, lbl]) => (
                        <button key={k} onClick={() => openDoc(e, k)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-200 flex items-center gap-1" data-testid={`exit-doc-${k}-${e.employee_id}`}><FileDown className="w-3 h-3" /> {lbl}</button>
                      ))}
                    </div>
                  )}
                  {e.exit.status !== "finalized" && <div className="text-[10px] text-amber-200/70 mt-1">Le bulletin de {e.exit.end_date?.slice(0, 7)} sera un solde de tout compte ; validez-le pour générer les documents.</div>}
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] text-slate-500 flex items-center gap-1" data-testid={`employee-link-${e.employee_id}`}><Link2 className="w-3 h-3" /> {e.user_id ? "Compte lié — espace « Mes bulletins » actif" : "Aucun compte lié"}</span>
                <div className="flex gap-1">
                  <button onClick={() => setDialog(e)} className="px-2 py-1 rounded text-[11px] text-slate-300 hover:bg-white/10 flex items-center gap-1" data-testid={`employee-edit-${e.employee_id}`}><Pencil className="w-3 h-3" /> Modifier</button>
                  {e.status !== "left" && <button onClick={() => setExitFor(e)} className="px-2 py-1 rounded text-[11px] text-amber-400 hover:bg-amber-500/10 flex items-center gap-1" data-testid={`employee-leave-${e.employee_id}`}><LogOut className="w-3 h-3" /> {e.exit ? "Modifier la sortie" : "Fin de contrat"}</button>}
                  <button onClick={() => remove(e)} className="px-2 py-1 rounded text-[11px] text-red-400 hover:bg-red-500/10" data-testid={`employee-delete-${e.employee_id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {exitFor && <ExitDialog api={api} employee={exitFor} onClose={(changed) => { setExitFor(null); if (changed) { load(); onChanged?.(); } }} />}
      {dialog && <EmployeeDialog api={api} societeId={societeId} employee={dialog.employee_id ? dialog : null} onClose={(changed) => { setDialog(null); if (changed) { load(); onChanged?.(); } }} />}
    </div>
  );
}
